/**
 * Coupons & Promos — UI data shapes + helpers.
 *
 * WIRED: data comes from the shipped `@v0y4ge/api-client` promos ops
 * (`listPromos`/`createPromo`/`updatePromo`/`duplicatePromo`/
 * `pausePromo`/`deletePromo`); the contract DTO (`PromoDiscount`) already
 * carries camelCase aliases, so the mapper is near-identity. No UI seed —
 * the dynamic mock (`PROMOS_CURATED_SEED`) is the pre-resolution source.
 */
export type DiscountType = "coupon" | "promo";
export type DiscountKind = "percent" | "fixed";
export type DiscountScope = "all" | "paid" | "specific";
export type DiscountStatus = "approved" | "draft";

export interface Discount {
  id: string;
  name: string;
  type: DiscountType;
  code: string;
  kind: DiscountKind;
  value: number;
  minSpend: number;
  uses: number;
  maxUses: number;
  perCustomer: number;
  scope: DiscountScope;
  starts: string;
  ends: string;
  status: DiscountStatus;
  paused: boolean;
  audience?: string;
  owner?: string;
  updated: string;
}

/** Contract PromoDiscount DTO → UI Discount (alias-tolerant, snake/camel). */
export function toDiscount(d: Record<string, any>): Discount {
  return {
    id: String(d.id ?? ""),
    name: d.name ?? "",
    type: d.type === "promo" ? "promo" : "coupon",
    code: d.code ?? "",
    kind: d.kind === "fixed" ? "fixed" : "percent",
    value: Number(d.value ?? 0),
    minSpend: Number(d.minSpend ?? d.min_spend ?? 0),
    uses: Number(d.uses ?? 0),
    maxUses: Number(d.maxUses ?? d.max_uses ?? 0),
    perCustomer: Number(d.perCustomer ?? d.per_customer ?? 0),
    scope: (d.scope === "paid" || d.scope === "specific") ? d.scope : "all",
    starts: d.starts ?? "Now",
    ends: d.ends ?? "At event start",
    status: d.status === "draft" ? "draft" : "approved",
    paused: !!d.paused,
    audience: d.audience ?? undefined,
    owner: d.owner ?? undefined,
    updated: d.updated ?? d.updatedAt ?? d.updated_at ?? "—",
  };
}

/** UI Discount → PromoCreateInput / PromoPatchInput — contract camelCase.
 * §2.1 (2026-07-26, most urgent): the schema declares minSpend/maxUses/
 * perCustomer and silently STRIPS unknown keys, so the old snake_case emit
 * (min_spend…) meant those three operator-entered values were never sent. */
export function toPromoInput(d: Discount): Record<string, unknown> {
  const isCoupon = d.type === "coupon";
  return {
    name: d.name,
    type: d.type,
    code: isCoupon ? String(d.code || "").toUpperCase().replace(/\s+/g, "") : "",
    kind: d.kind,
    value: d.value,
    minSpend: d.minSpend,
    maxUses: d.maxUses,
    perCustomer: d.perCustomer,
    scope: d.scope,
    starts: d.starts,
    ends: d.ends,
    status: d.status,
    paused: d.paused,
    audience: d.audience ?? null,
  };
}

export const discountValueLabel = (d: Discount): string =>
  d.kind === "percent" ? `${d.value}% off` : `$${d.value} off`;

let _dseq = 200;
export const newDiscountId = (p = "d"): string => `${p}_${(++_dseq).toString(36)}${Date.now().toString(36).slice(-3)}`;

export function newDiscount(type: DiscountType = "coupon"): Discount {
  return {
    id: newDiscountId("d"), name: "", type, code: "", kind: "percent", value: 10,
    minSpend: 0, uses: 0, maxUses: type === "coupon" ? 100 : 0, perCustomer: type === "coupon" ? 1 : 0,
    scope: "all", starts: "Now", ends: "At event start", status: "draft", paused: false,
    owner: "You", updated: "Just now",
  };
}

/**
 * INTERIM seed — used only when the promos client ops aren't available (e.g.
 * the served preview bundles against a STUB `@v0y4ge/api-client`, and the CD
 * kit has no `@v0y4ge/api-mocks`). The real contract is shipped; once the mock
 * store serves `listPromos`, this fallback is bypassed. Tenant-agnostic.
 */
const _D = (o: Partial<Discount> & Pick<Discount, "id" | "name" | "type">): Discount => ({
  code: "", kind: "percent", value: 10, minSpend: 0, uses: 0, maxUses: 0, perCustomer: 0,
  scope: "all", starts: "Now", ends: "At event start", status: "approved", paused: false, updated: "—", ...o,
});
export const PROMO_LIBRARY: Discount[] = []; // #635: baked promos removed — served via listPromos / @v0y4ge/api-mocks.

